import React from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Link, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/contexts/AuthContext";
import { TourProvider } from "@/contexts/TourContext";
import InterviewTranscript from "@/pages/InterviewTranscript";
import DemoPlayer from "@/pages/DemoPlayer";
import ProtectedRoute from "@/components/ProtectedRoute";

import LandingPage from "@/pages/LandingPage";
import ConsultingChat from "@/pages/ConsultingChat";
import ExecutiveReport from "@/pages/ExecutiveReport";
import AgentChat from "@/pages/AgentChat";
import AgentEdit from "@/pages/AgentEdit";
import Dashboard from "@/pages/Dashboard";
import AgentMetrics from "@/pages/AgentMetrics";
import AdminPlans from "@/pages/AdminPlans";
import AdminBilling from "@/pages/AdminBilling";
import KblLibrary from "@/pages/KblLibrary";
import AdminLlmKeys from "@/pages/AdminLlmKeys";
import AdminIndustry from "@/pages/AdminIndustry";
import AdminUsers from "@/pages/AdminUsers";
import AdminTenants from "@/pages/AdminTenants";
import AdminDemo from "@/pages/AdminDemo";
import AgentMcps from "@/pages/AgentMcps";
import FlowDocument from "@/pages/FlowDocument";
import AgentQa from "@/pages/AgentQa";
import AdminCompanies from "@/pages/AdminCompanies";
import AdminCompanyDetail from "@/pages/AdminCompanyDetail";
import MyCompany from "@/pages/MyCompany";
import MyCompanyKeys from "@/pages/MyCompanyKeys";
import { ThemeProvider } from "@/contexts/ThemeContext";
import Login from "@/pages/Login";
import Register from "@/pages/Register";

// Fix 7 (N16): unknown URLs no longer render a blank page
function NotFound() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white px-6 text-center" data-testid="not-found">
      <p className="eyebrow text-accent-blue">Error 404</p>
      <h1 className="font-display text-3xl text-slate-900">Página no encontrada</h1>
      <p className="text-sm text-slate-600">La dirección no existe o fue movida.</p>
      <Link to="/" className="text-sm font-semibold text-accent-blue hover:underline">Volver al inicio →</Link>
    </div>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-white text-slate-900">
      <AuthProvider>
        <ThemeProvider>
        <BrowserRouter>
          <TourProvider>
          <Routes>
            {/* Public surfaces */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/consulta" element={<ConsultingChat />} />
            <Route path="/informe/:sid" element={<ExecutiveReport />} />
            <Route path="/agentes/:slug" element={<AgentChat />} />

            {/* Authenticated surfaces (any approved role) */}
            <Route path="/dashboard" element={
              <ProtectedRoute><Dashboard /></ProtectedRoute>
            } />
            <Route path="/agentes/:slug/pruebas" element={
              <ProtectedRoute roles={["admin"]}><AgentQa /></ProtectedRoute>
            } />
            <Route path="/agentes/:slug/metricas" element={
              <ProtectedRoute><AgentMetrics /></ProtectedRoute>
            } />

            {/* Client company (cliente role: master and normal users) */}
            <Route path="/mi-empresa" element={
              <ProtectedRoute roles={["cliente"]}><MyCompany /></ProtectedRoute>
            } />
            <Route path="/mi-empresa/claves" element={
              <ProtectedRoute roles={["cliente"]}><MyCompanyKeys /></ProtectedRoute>
            } />

            {/* Admin / Consultor surfaces */}
            <Route path="/demo/:token" element={<DemoPlayer />} />
            <Route path="/informe/:sid/flujo" element={<FlowDocument />} />
            <Route path="/informe/:sid/entrevista" element={
              <ProtectedRoute roles={["admin", "consultor"]}><InterviewTranscript /></ProtectedRoute>
            } />
            <Route path="/agentes/:slug/editar" element={
              <ProtectedRoute roles={["admin", "consultor"]}><AgentEdit /></ProtectedRoute>
            } />
            <Route path="/agentes/:slug/mcps" element={
              <ProtectedRoute roles={["admin", "consultor"]}><AgentMcps /></ProtectedRoute>
            } />
            <Route path="/admin/planes" element={
              <ProtectedRoute roles={["admin"]}><AdminPlans /></ProtectedRoute>
            } />
            <Route path="/admin/facturacion" element={
              <ProtectedRoute roles={["admin"]}><AdminBilling /></ProtectedRoute>
            } />
            <Route path="/admin/kbl" element={
              <ProtectedRoute roles={["admin", "consultor"]}><KblLibrary /></ProtectedRoute>
            } />
            <Route path="/admin/industria" element={
              <ProtectedRoute roles={["admin", "consultor"]}><AdminIndustry /></ProtectedRoute>
            } />

            {/* Admin-only */}
            <Route path="/admin/llm" element={
              <ProtectedRoute roles={["admin"]}><AdminLlmKeys /></ProtectedRoute>
            } />
            <Route path="/admin/usuarios" element={
              <ProtectedRoute roles={["admin"]}><AdminUsers /></ProtectedRoute>
            } />
            <Route path="/admin/demo" element={
              <ProtectedRoute roles={["admin"]}><AdminDemo /></ProtectedRoute>
            } />
            <Route path="/admin/clientes" element={
              <ProtectedRoute roles={["admin"]}><AdminCompanies /></ProtectedRoute>
            } />
            <Route path="/admin/clientes/:slug" element={
              <ProtectedRoute roles={["admin"]}><AdminCompanyDetail /></ProtectedRoute>
            } />
            <Route path="/admin/tenants" element={
              <ProtectedRoute roles={["admin"]}><AdminTenants /></ProtectedRoute>
            } />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </TourProvider>
        </BrowserRouter>
        </ThemeProvider>
      </AuthProvider>
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "#0f172a",
            color: "#f8fafc",
            border: "1px solid #334155",
            borderRadius: 2,
            fontFamily: "IBM Plex Sans, sans-serif",
          },
        }}
      />
    </div>
  );
}

export default App;
